import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import styles from '../AdminPortal.module.scss';
import { TpOrganizationInfo } from '../../../api/models/admin';
import { TpUserInfo } from '../../../data/interfaces';
import { UserOrganization } from '../../../api/models/apis';
import {
  promptDeleteUser,
  promptModifyUser,
  promptReassignUserActiveOrganization,
  promptRemoveUserFromOrganization,
} from '../actions/m2mCrudUser';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { useM2MApiContext } from '../../../api/m2mApi';
import { useUser } from '../../../app/hooks';

type UserOrganizationListItem = UserOrganization & { userEmail: string };

interface TpAdminUserOrganizationsProps {
  users: TpUserInfo[];
  organizations: TpOrganizationInfo[];
  selectedUser?: string; // filter data to single user
  selectedOrganization?: string; // filter data to single organization
  allowModifyUser?: boolean;
  allowRemoveUser?: boolean;
  allowDeleteUser?: boolean;
  setIsLoading: (isLoading: boolean, message?: string) => void;
  updateUsers: () => void;
  updateOrganizations: () => void;
}

/**
 * Component to display a marker icon indicating whether a role is present or absent.
 */
function RoleMarker(props: { hasRole: boolean }) {
  return props.hasRole ? (
    <FontAwesomeIcon className={styles.roleIsPresent} icon={['fas', 'circle-check']} />
  ) : (
    <FontAwesomeIcon className={styles.roleIsAbsent} icon={['fas', 'xmark']} />
  );
}

/**
 * A component containing a table listing a set of UserOrganization elements containing
 * either the organizations a user belongs to, or the users that belong to an organization.
 * The list contains columns with the isActive status and roles as well as action buttons to
 * manage the memberships.
 */
export function UserOrganizations(props: TpAdminUserOrganizationsProps) {
  const m2MApi = useM2MApiContext();
  const { user: loggedInUser } = useUser();

  let items: UserOrganizationListItem[] = props.users
    .filter((user) => user.email !== undefined)
    .flatMap((user) => {
      return user.organizations
        .filter((organization) => organization.userId !== undefined)
        .map((organization) => {
          return {
            ...organization,
            userEmail: user.email!,
          };
        });
    })
    .sort((a, b) => {
      if (a.organizationName === b.organizationName) {
        return a.userEmail.localeCompare(b.userEmail);
      }
      return a.organizationName.localeCompare(b.organizationName);
    });

  const showUserColumn = props.selectedUser === undefined;
  const showOrganizationColumn = props.selectedOrganization === undefined;

  if (props.selectedUser) {
    items = items.filter((item) => item.userEmail === props.selectedUser);
  }

  if (props.selectedOrganization) {
    items = items.filter((item) => item.organizationId === props.selectedOrganization);
  }

  function findUserAndOrganization(userOrganizationInfo: UserOrganizationListItem): {
    targetUser: TpUserInfo;
    targetOrganization: UserOrganization;
  } {
    const targetUser: TpUserInfo | undefined = props.users.find((usr) => {
      return usr.email === userOrganizationInfo.userEmail;
    });
    const targetOrganization: UserOrganization | undefined = targetUser?.organizations.find((org) => {
      return org.organizationId === userOrganizationInfo.organizationId;
    });
    if (!targetUser || !targetOrganization) {
      //Should not happen
      throw new Error('Could not find user or organization for action on user organization');
    }
    return { targetUser, targetOrganization };
  }

  // Helper to centralize the duplicated logic of the three handlers
  function performUserOrgAction(message: string, operation: () => Promise<any>) {
    props.setIsLoading(true, message);
    operation()
      .then(() => {
        props.updateUsers();
        props.updateOrganizations();
      })
      .catch((err) => {
        if (err === undefined) return;
        const defaultMessage = 'Operation failed.';
        const errMessage = err?.response?.data?.detail?.detail;
        showErrorMessage(typeof errMessage == 'string' ? errMessage : defaultMessage);
      })
      .finally(() => {
        props.setIsLoading(false);
      });
  }

  function handleModifyUserAction(userOrganizationInfo: UserOrganizationListItem) {
    const { targetUser, targetOrganization } = findUserAndOrganization(userOrganizationInfo);
    performUserOrgAction(
      `Modifying user ${userOrganizationInfo.userEmail} in organization ${userOrganizationInfo.organizationName}`,
      () => promptModifyUser(m2MApi, targetUser, targetOrganization),
    );
  }

  function handleRemoveUserFromOrgAction(userOrganizationInfo: UserOrganizationListItem) {
    const { targetUser, targetOrganization } = findUserAndOrganization(userOrganizationInfo);
    performUserOrgAction(
      `Removing user ${userOrganizationInfo.userEmail} from organization ${userOrganizationInfo.organizationName}`,
      () => promptRemoveUserFromOrganization(m2MApi, targetUser, targetOrganization.organizationName),
    );
  }

  function handleReassignUserActiveOrganization(userOrganizationInfo: UserOrganizationListItem) {
    const { targetUser, targetOrganization } = findUserAndOrganization(userOrganizationInfo);
    performUserOrgAction(
      `Reassigning user ${userOrganizationInfo.userEmail} from organization ${userOrganizationInfo.organizationName}`,
      () => promptReassignUserActiveOrganization(m2MApi, targetUser, targetOrganization),
    );
  }

  function handleDeleteUserAction(userOrganizationInfo: UserOrganizationListItem) {
    const { targetUser } = findUserAndOrganization(userOrganizationInfo);
    performUserOrgAction(`Deleting user ${userOrganizationInfo.userEmail}`, () => promptDeleteUser(m2MApi, targetUser));
  }

  return (
    <div className={styles.organizationDetailBlockList}>
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              {showOrganizationColumn && <th style={{ width: 200 }}>Organization</th>}
              {showUserColumn && <th style={{ width: 200 }}>User Email</th>}
              <th style={{ width: 80, textAlign: 'center' }}>Is Active</th>
              <th style={{ width: 80, textAlign: 'center' }}>Can Write</th>
              <th style={{ width: 100, textAlign: 'center' }}>Organization Admin</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((userOrganizationInfo) => {
              return (
                <tr key={`${userOrganizationInfo.organizationId}${userOrganizationInfo.userId}`}>
                  {showOrganizationColumn && (
                    <td>
                      <b>{userOrganizationInfo.organizationName}</b>
                    </td>
                  )}
                  {showUserColumn && (
                    <td>
                      <b>{userOrganizationInfo.userEmail}</b>
                    </td>
                  )}
                  <td style={{ textAlign: 'center' }}>
                    <RoleMarker hasRole={!!userOrganizationInfo.isActive} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <RoleMarker hasRole={!!userOrganizationInfo.canWrite} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <RoleMarker hasRole={!!userOrganizationInfo.isOrganizationAdmin} />
                  </td>
                  <td>
                    {props.allowModifyUser && (
                      <FontAwesomeIcon
                        className={styles.tableActionButton}
                        icon={['fas', 'user-gear']}
                        title="Modify User"
                        onClick={() => handleModifyUserAction(userOrganizationInfo)}
                      />
                    )}
                    {props.allowDeleteUser && (
                      <FontAwesomeIcon
                        className={styles.tableActionButton}
                        icon={['fas', 'trash-can']}
                        title="Delete User"
                        onClick={() => handleDeleteUserAction(userOrganizationInfo)}
                      />
                    )}
                    {userOrganizationInfo.isActive &&
                      props.allowRemoveUser &&
                      userOrganizationInfo.userEmail.toLowerCase() !== loggedInUser.email?.toLowerCase() && (
                        <FontAwesomeIcon
                          className={styles.tableActionButton}
                          icon={['far', 'repeat']}
                          title="Assign User to other organization"
                          onClick={() => handleReassignUserActiveOrganization(userOrganizationInfo)}
                        />
                      )}
                    {!userOrganizationInfo.isActive && props.allowRemoveUser && (
                      <FontAwesomeIcon
                        className={styles.tableActionButton}
                        icon={['fas', 'arrow-right-from-bracket']}
                        title="Remove User from Organization"
                        onClick={() => handleRemoveUserFromOrgAction(userOrganizationInfo)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
